import React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { useExpandableDropdown } from './expandable-dropdown';
import { cn } from '../../utils/cn';

interface ExpandableDropdownTriggerProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
    asChild?: boolean;
}

export const ExpandableDropdownTrigger = React.forwardRef<
    HTMLButtonElement,
    ExpandableDropdownTriggerProps
>(
    (
        { className, children, asChild = false, disabled = false, ...props },
        ref,
    ) => {
        const { open, setOpen, triggerRef } = useExpandableDropdown();

        const handleRef = (node: HTMLButtonElement | null) => {
            triggerRef.current = node;
            if (typeof ref === 'function') {
                ref(node);
                return;
            }
            if (ref) {
                ref.current = node;
            }
        };

        const Comp = asChild ? Slot : 'button';

        return (
            <Comp
                ref={handleRef}
                type={asChild ? undefined : 'button'}
                className={cn(
                    'outline-none focus-visible:outline-none',
                    open && 'pointer-events-none',
                    className,
                )}
                onClick={() => {
                    if (disabled) return;
                    setOpen(!open);
                }}
                aria-expanded={open}
                {...props}
            >
                {children}
            </Comp>
        );
    },
);

ExpandableDropdownTrigger.displayName = 'ExpandableDropdownTrigger';
